// Online payments on the payment provider (F-PAY-03; spec/06 §9.1–§9.3, §9.6, spec/08 §4.8, §4.18).
//
// An **online payment** is one the player makes to the payment provider — by scanning a UPI QR at the
// counter, or on the provider's checkout — rather than one the counter takes. The platform asks the
// provider for a **payment intent** for an amount, shows the player what to pay it with, and learns
// that it was paid only from the provider: its webhook, or the reconciliation job asking it
// (spec/06 §9.3). A client never tells the server a payment succeeded; it polls the intent.
//
//   POST …/venues/{v}/sales/{saleId}/payment-intents                 pay a sale, or part of it, online
//   GET  …/venues/{v}/payment-intents/{paymentIntentId}              poll an intent
//   POST …/venues/{v}/payment-intents/{paymentIntentId}/cancel       abandon an open intent
//   GET  …/venues/{v}/payments/online                                is online payment open here, and if not, why
//   POST …/players/{playerId}/wallet/online-top-ups                  top a wallet up online (F-WAL-02)
//   POST /v1/webhooks/payments/{provider}                            the provider's webhook (unauthenticated)
//   GET  /v1/payments/checkout/{provider}                            the page a provider's checkout opens from
//                                                                    (unauthenticated; ADR-0061)
//
// **Provider-neutral** (ADR-0061): nothing here names a provider. A client shows `qrPayload` as a QR, opens
// `checkoutUrl`, and polls the intent; with Cashfree the QR and the checkout are one URL, the platform's own
// checkout page, which opens Cashfree's hosted checkout (UPI, cards, netbanking) on the player's phone.
//
// When a sale's intent captures, the money is recorded as a sale payment with tender `online` and the
// sale settles when it is covered (spec/06 §9.1); when a wallet top-up's intent captures, the wallet is
// credited with tender `online` once (spec/06 §2.3 `wallet_topup` online). An intent that fails, is
// cancelled or expires records nothing. Money is an integer minor unit plus an ISO code throughout.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { PaymentIntentIdSchema, PlayerIdSchema, SaleIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { NonNegativeMoneySchema } from "../primitives/money.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema, VenueScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { IdempotentCreateHeaderSchema } from "./id-checks.js";
import { ORDERS_CREATE, ORDERS_READ, WALLET_TOPUP } from "../permissions/index.js";
// ---------------------------------------------------------------------------------------------
// Shared vocabulary (spec/06 §9.2)
// ---------------------------------------------------------------------------------------------
/**
 * Where an intent is in its life (spec/06 §9.2): `pending` → `succeeded` | `failed` | `cancelled` |
 * `expired`. Only the provider — its webhook, or the reconciliation job asking it — moves an intent to
 * `succeeded` (spec/06 §9.3).
 */
export const PaymentIntentStatusSchema = z
    .enum(['pending', 'succeeded', 'failed', 'cancelled', 'expired'])
    .openapi('PaymentIntentStatus', { description: 'Where an online payment intent is in its life (spec/06 §9.2).' });
/** What an intent pays for: a sale (spec/06 §8), or a wallet top-up (spec/06 §5, F-WAL-02). */
export const PaymentIntentPurposeSchema = z
    .enum(['sale', 'wallet_topup'])
    .openapi('PaymentIntentPurpose', { description: 'What an online payment intent pays for (spec/06 §9.3).' });
/**
 * An online payment intent, with what the client needs to pay it (spec/06 §9.3): a UPI intent URL and
 * a QR payload for the counter and the kiosk, the provider's checkout for the app and the web, and a
 * short link to send.
 */
export const PaymentIntentSchema = z
    .object({
    id: PaymentIntentIdSchema,
    purpose: PaymentIntentPurposeSchema,
    /** The sale it pays, for a `sale` intent; null for a wallet top-up. */
    saleId: SaleIdSchema.nullable(),
    /** The player whose wallet it tops up, for a `wallet_topup` intent; the sale's player, if any, otherwise. */
    playerId: PlayerIdSchema.nullable(),
    status: PaymentIntentStatusSchema,
    /** What the player is asked to pay. At least ₹1 (spec/06 §9.3). */
    amount: NonNegativeMoneySchema,
    /** What the provider captured, once it has; null until then. */
    capturedAmount: NonNegativeMoneySchema.nullable(),
    /** How the player paid, once the provider says: `upi`, `card`, `netbanking` or `wallet_app` (spec/06 §9.2). */
    method: z.string().min(1).max(40).nullable(),
    /** The payment provider that holds it (`fake` in local development). */
    provider: z.string().min(1).max(40),
    /**
     * A `upi://pay?…` link a UPI app opens (spec/06 §9.3) — **empty** when the provider offers no UPI link of its
     * own and is paid through `checkoutUrl` (Cashfree, ADR-0061).
     */
    upiIntentUrl: z.string().max(2000),
    /** What the QR code at the counter or the kiosk encodes: a UPI link, or the checkout page the phone opens. */
    qrPayload: z.string().min(1).max(2000),
    /** The provider's hosted checkout, for the app and the web (UPI, cards and netbanking with Cashfree). */
    checkoutUrl: z.string().min(1).max(2000),
    /** A short payment link to send by SMS or WhatsApp. */
    shortLink: z.string().min(1).max(2000),
    /** When the intent stops being payable (`payments.intentTtlMinutes` after it was created). */
    expiresAt: IsoDateTimeSchema,
    /** Why the provider refused it, when it failed; null otherwise. */
    failureReason: z.string().min(1).max(500).nullable(),
    createdAt: IsoDateTimeSchema,
    /** When the provider reported it captured; null until then. */
    capturedAt: IsoDateTimeSchema.nullable(),
})
    .openapi('PaymentIntent', { description: 'An online payment intent and what the client needs to pay it (spec/06 §9.3).' });
/**
 * Whether online payments are open at a venue and, if not, which gate is closed (spec/06 §9.5: "the
 * console shows which gate is closed and why"). `open`: every gate is open. `provider_not_configured`:
 * the deployment has no payment provider. `payee_not_active`: the provider pays the organisation through a
 * payee (ADR-0061) and it has none yet, or it is not active (being verified, needing action, refused or
 * suspended). `disabled`: the venue has not turned online payments on (`payments.onlineEnabled`).
 */
export const OnlinePaymentsGateSchema = z
    .enum(['open', 'provider_not_configured', 'disabled', 'payee_not_active'])
    .openapi('OnlinePaymentsGate', { description: 'Which gate on online payments is closed, or `open` (spec/06 §9.5).' });
/**
 * Where the provider is with the organisation's payee, as a venue sees it (read-only; HQ onboards it):
 * `not_onboarded` until HQ has, then the payee's status. Null when the provider does not pay through payees.
 */
export const OnlinePaymentsPayeeStatusSchema = z
    .enum(['not_onboarded', 'pending', 'action_required', 'active', 'rejected', 'suspended'])
    .openapi('OnlinePaymentsPayeeStatus', { description: 'The organisation’s payee at the payment provider, as a venue sees it (ADR-0061).' });
export const OnlinePaymentsStatusSchema = z
    .object({
    enabled: z.boolean(),
    gate: OnlinePaymentsGateSchema,
    /** The configured provider's name, when there is one. */
    provider: z.string().min(1).max(40).nullable(),
    /** The organisation's payee, when the provider pays through one (ADR-0061); null otherwise. */
    payeeStatus: OnlinePaymentsPayeeStatusSchema.nullable(),
})
    .openapi('OnlinePaymentsStatus', { description: 'Whether online payments are open at a venue (spec/06 §9.5).' });
// ---------------------------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------------------------
/**
 * Pay a sale online (spec/06 §9.3). Without `amount` the intent is for everything outstanding; with
 * one, a part of it (split tender, at least ₹1 and at most the outstanding). A sale has at most one
 * open intent: creating a new one cancels the previous one.
 */
export const CreateSalePaymentIntentSchema = z
    .object({ amount: NonNegativeMoneySchema.optional() })
    .strict()
    .openapi('CreateSalePaymentIntentRequest');
/** Top a wallet up online (F-WAL-02): the cash to add and an optional top-up offer, as at the counter. */
export const WalletOnlineTopUpRequestSchema = z
    .object({
    amount: NonNegativeMoneySchema,
    offerId: z.string().min(1).max(64).optional(),
})
    .strict()
    .openapi('WalletOnlineTopUpRequest', { description: 'Begin an online top-up of a player’s wallet (F-WAL-02).' });
/** What the webhook answers a delivery it accepted. `duplicate` is not reported: de-duplication happens after. */
export const PaymentWebhookAckSchema = z
    .object({ received: z.boolean() })
    .openapi('PaymentWebhookAck', { description: 'A provider webhook delivery accepted for processing.' });
// ---------------------------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------------------------
const SalePaymentIntentParamsSchema = VenueScopeParamsSchema.extend({ saleId: SaleIdSchema }).openapi('SalePaymentIntentParams');
const PaymentIntentParamsSchema = VenueScopeParamsSchema.extend({ paymentIntentId: PaymentIntentIdSchema }).openapi('PaymentIntentParams');
const WalletOnlineTopUpParamsSchema = OrganizationScopeParamsSchema.extend({ playerId: PlayerIdSchema }).openapi('WalletOnlineTopUpParams');
const PaymentWebhookParamsSchema = z
    .object({ provider: z.string().min(1).max(40).regex(/^[a-z][a-z0-9_]*$/) })
    .openapi('PaymentWebhookParams');
export const createSalePaymentIntentRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sales/{saleId}/payment-intents',
    operationId: 'createSalePaymentIntent',
    tags: ['payments'],
    summary: 'Pay a sale online (F-PAY-03)',
    description: 'Asks the payment provider for an intent for the outstanding amount, or part of it for split tender (at least ' +
        '₹1), and returns the UPI link, QR payload and checkout to pay it with. A sale has at most one open intent: a ' +
        'new one cancels the previous. Nothing is recorded as paid here — the sale is paid only when the provider ' +
        'reports the capture (spec/06 §9.3); poll the intent. Needs `payments.onlineEnabled` at the venue and a ' +
        'configured provider. Requires `orders:create` and an `Idempotency-Key`. Audited.',
    [PERMISSION_EXTENSION]: ORDERS_CREATE,
    request: {
        params: SalePaymentIntentParamsSchema,
        headers: IdempotentCreateHeaderSchema,
        body: { content: { 'application/json': { schema: CreateSalePaymentIntentSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(PaymentIntentSchema, 'The open intent, with what the player pays it with.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such sale at this venue.', 'not-found'),
        409: problemResponse('Online payments are closed here, or the sale is cancelled, already paid, or the amount exceeds what is outstanding.', 'online-payments-unavailable', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
        503: problemResponse('The payment provider could not be reached. Try again.', 'payment-provider-unavailable'),
    },
});
export const getPaymentIntentRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/payment-intents/{paymentIntentId}',
    operationId: 'getPaymentIntent',
    tags: ['payments'],
    summary: 'Read an online payment intent (F-PAY-03)',
    description: 'The intent as the server last recorded it — poll it while the player pays. It moves to `succeeded` only when the ' +
        'provider reports the capture. Requires `orders:read`.',
    [PERMISSION_EXTENSION]: ORDERS_READ,
    request: { params: PaymentIntentParamsSchema },
    responses: {
        200: jsonResponse(PaymentIntentSchema, 'The intent.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such intent at this venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const cancelPaymentIntentRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/payment-intents/{paymentIntentId}/cancel',
    operationId: 'cancelPaymentIntent',
    tags: ['payments'],
    summary: 'Cancel an open online payment intent (F-PAY-03)',
    description: 'Abandons a `pending` intent and cancels it at the provider where it can. Cancelling one that is already closed ' +
        'answers it as it is. An intent the provider has captured cannot be cancelled (`conflict`). Requires ' +
        '`orders:create`. Audited.',
    [PERMISSION_EXTENSION]: ORDERS_CREATE,
    request: { params: PaymentIntentParamsSchema },
    responses: {
        200: jsonResponse(PaymentIntentSchema, 'The intent after the cancel.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such intent at this venue.', 'not-found'),
        409: problemResponse('The provider has captured the intent; it is paid, not cancellable.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getOnlinePaymentsStatusRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/payments/online',
    operationId: 'getOnlinePaymentsStatus',
    tags: ['payments'],
    summary: 'Whether online payments are open at a venue (F-PAY-03)',
    description: 'Whether the counter can offer an online tender here, and if not which gate is closed: no provider configured, ' +
        'or `payments.onlineEnabled` off for the venue (spec/06 §9.5). Requires `orders:read`.',
    [PERMISSION_EXTENSION]: ORDERS_READ,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(OnlinePaymentsStatusSchema, 'The gates.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const beginOnlineWalletTopUpRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/players/{playerId}/wallet/online-top-ups',
    operationId: 'beginOnlineWalletTopUp',
    tags: ['wallet'],
    summary: 'Top up a player’s wallet online (F-WAL-02, F-PAY-03)',
    description: 'Checks the top-up against the wallet’s limits and offers, then asks the payment provider for an intent and ' +
        'returns what the player pays it with. **Nothing is credited here**: the wallet is credited once, with tender ' +
        '`online`, when the provider reports the capture (spec/06 §9.3). Name the venue with `X-Clavian-Venue`; it needs ' +
        '`payments.onlineEnabled` there. Requires `wallet:topup` and an `Idempotency-Key`. Audited.',
    [PERMISSION_EXTENSION]: WALLET_TOPUP,
    request: {
        params: WalletOnlineTopUpParamsSchema,
        headers: IdempotentCreateHeaderSchema,
        body: { content: { 'application/json': { schema: WalletOnlineTopUpRequestSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(PaymentIntentSchema, 'The open intent, with what the player pays it with.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation or player, or no venue you can see named in X-Clavian-Venue.', 'not-found'),
        409: problemResponse('Online payments are closed here, the wallet is not enabled, or the amount is outside its limits.', 'online-payments-unavailable', 'wallet-disabled', 'wallet-limit', 'wallet-topup-tax-at-sale'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
        503: problemResponse('The payment provider could not be reached. Try again.', 'payment-provider-unavailable'),
    },
});
export const paymentsWebhookRoute = createRoute({
    method: 'post',
    path: '/v1/webhooks/payments/{provider}',
    operationId: 'receivePaymentsWebhook',
    tags: ['payments'],
    summary: 'Receive a payment provider’s webhook (F-PAY-03)',
    description: 'Unauthenticated by session: the provider’s signature is verified over the raw body, and a delivery more than ' +
        'five minutes old is refused. An accepted delivery is stored and processed once, de-duplicated by the ' +
        'provider’s event id; a capture records the payment idempotently by the provider’s payment id (spec/06 §9.3).',
    request: {
        params: PaymentWebhookParamsSchema,
        body: {
            content: { 'application/json': { schema: z.record(z.string(), z.unknown()).openapi('PaymentWebhookBody') } },
            required: true,
        },
    },
    responses: {
        200: jsonResponse(PaymentWebhookAckSchema, 'Accepted for processing.'),
        400: commonProblemResponses[400],
        401: problemResponse('The signature does not match the raw body, or the delivery is stale.', 'webhook-signature-invalid'),
        404: problemResponse('No payment provider by that name is configured.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/**
 * The page a provider's hosted checkout is opened from (ADR-0061), for a provider whose checkout is started by its
 * own script rather than reached at a plain URL (Cashfree). The checkout session rides in the URL's fragment, which
 * never reaches the server. A self-contained HTML page with its own content policy; no session, no cookie.
 */
export const paymentsCheckoutPageRoute = createRoute({
    method: 'get',
    path: '/v1/payments/checkout/{provider}',
    operationId: 'getPaymentsCheckoutPage',
    tags: ['payments'],
    summary: 'The page a payment provider’s checkout opens from (ADR-0061)',
    description: 'An HTML page that opens the configured provider’s hosted checkout for the payment session named in the URL’s ' +
        'fragment (`#…`, never sent to the server). What `checkoutUrl` and `qrPayload` point at for a provider without a ' +
        'plain checkout URL. Unauthenticated; `404` when no provider by that name is configured or it needs no page.',
    request: { params: PaymentWebhookParamsSchema },
    responses: {
        200: { description: 'The checkout page.', content: { 'text/html': { schema: z.string() } } },
        400: commonProblemResponses[400],
        404: problemResponse('No payment provider by that name is configured, or it needs no checkout page.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=payments.js.map